import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { BRAND } from "@/lib/site";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-[#18181B] text-zinc-200 mt-24">
      {/* Marching CTA rule */}
      <div className="border-y border-zinc-700 overflow-hidden">
        <div className="ticker-track py-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={i}
              className="font-display text-4xl md:text-5xl font-black uppercase text-zinc-100 whitespace-nowrap px-8 flex items-center gap-8"
            >
              Diagnose <span className="text-[#D94515]">·</span> Break Out{" "}
              <span className="text-[#D94515]">·</span> Treat{" "}
              <span className="text-[#D94515]">·</span> Rebuild{" "}
              <span className="text-[#D94515]">·</span>
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Link to="/" className="inline-block bg-white px-4 py-3 hover:bg-zinc-100 transition-colors" data-testid="footer-logo-link">
            <img
              src={BRAND.logoUrl}
              alt={BRAND.name}
              className="h-11 md:h-12 w-auto"
              loading="lazy"
            />
          </Link>
          <p className="mt-6 text-sm leading-relaxed text-zinc-400 max-w-md">
            Specialist diagnosis and repair of reinforced concrete affected by
            chemical attack (chlorides, carbonation, sulphates, ASR),
            reinforcement corrosion and fire damage. Investigation, breakout,
            treatment and full reinstatement, one accountable team.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="tech-label mb-4 text-zinc-500">// Navigate</div>
          <ul className="space-y-2 text-sm">
            {[
              ["/", "Home"],
              ["/services", "Services"],
              ["/mechanisms", "Mechanisms"],
              ["/case-studies", "Case Studies"],
              ["/products", "Products"],
              ["/about", "About"],
              ["/contact", "Contact"],
            ].map(([to, label]) => (
              <li key={to}>
                <Link to={to} className="text-zinc-300 hover:text-[#D94515] transition-colors" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="tech-label mb-4 text-zinc-500">// Contact</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Phone size={14} className="mt-1 text-[#D94515]" />
              <span data-testid="footer-phone">{BRAND.phone}</span>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={14} className="mt-1 text-[#D94515]" />
              <span data-testid="footer-email">{BRAND.email}</span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={14} className="mt-1 text-[#D94515]" />
              <span>{BRAND.areaOfOperation}</span>
            </li>
          </ul>

          <Link to="/contact" className="btn-outline on-dark mt-6" data-testid="footer-cta-enquire">
            Request Site Visit <ArrowUpRight size={14} strokeWidth={2.5} />
          </Link>
        </div>
      </div>

      <div className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
          <div>© {year} {BRAND.name} · All rights reserved</div>
          <div className="flex flex-wrap items-center gap-4 md:gap-6">
            <Link to="/privacy" className="hover:text-[#D94515] transition-colors" data-testid="footer-link-privacy">Privacy</Link>
            <Link to="/cookies" className="hover:text-[#D94515] transition-colors" data-testid="footer-link-cookies">Cookies</Link>
            <Link to="/terms" className="hover:text-[#D94515] transition-colors" data-testid="footer-link-terms">Terms</Link>
            <span className="hidden md:inline">Reg. England &amp; Wales</span>
            <span className="hidden md:inline">EN 1504 Methodology</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
